<template>
  <Form
    :initial-values="initialValues"
    class="flex flex-col gap-3"
    v-slot="{ values, setFieldValue, resetField }"
    @submit="(values) => $emit('onSubmitNewAddress', values)"
  >
    <div class="flex flex-col md:flex-row">
      <Field :rules="requiredRule" name="emirateId" v-slot="{ field, errors: errs, handleChange }">
        <Dropdown
          :field="field"
          :errors="errs"
          :label="$t('emirate')"
          :options="addressMgmtStore.emirates"
          :is-read-only="isReadOnly"
          class="px-1.5 w-full md:w-1/2"
          direction="flex-col"
          @on-selection="
            (item) => {
              if (!item) {
                resetField('emirateId')
                resetField('cityId')
                resetField('cityZoneId')
                return
              }
              handleChange(+item?.id)
            }
          "
        ></Dropdown>
      </Field>
      <Field :rules="requiredRule" name="cityId" v-slot="{ field, errors: errs, handleChange }">
        <Dropdown
          :key="values.emirateId"
          :field="field"
          :errors="errs"
          :label="$t('city')"
          :options="addressMgmtStore.emirateCities[values.emirateId]?.cities"
          :is-read-only="isReadOnly"
          class="px-1.5 w-full md:w-1/4"
          direction="flex-col"
          @on-selection="
            (item) => {
              if (!item) {
                resetField('cityId')
                resetField('cityZoneId')
                return
              }
              handleChange(+item?.id)
            }
          "
        ></Dropdown>
      </Field>
      <Field :rules="requiredRule" name="cityZoneId" v-slot="{ field, errors: errs, handleChange }">
        <Dropdown
          :key="`${values.emirateId}-${values.cityId}`"
          :field="field"
          :errors="errs"
          :label="$t('area')"
          :options="addressMgmtStore.cityZones.filter((cz) => cz.cityId == values.cityId)"
          :is-read-only="isReadOnly"
          class="px-1.5 w-full md:w-1/4"
          direction="flex-col"
          @on-selection="(item) => handleChange(+item?.id)"
        ></Dropdown>
      </Field>
    </div>
    <div class="flex flex-col md:flex-row">
      <Field :rules="requiredRule" name="buildingName" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('buildingName')"
          :is-read-only="isReadOnly"
          class="px-1.5 w-full md:w-1/4"
          direction="flex-col"
        />
      </Field>
      <Field :rules="requiredRule" name="appartmentNumber" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('flatNumber')"
          :is-read-only="isReadOnly"
          class="px-1.5 w-full md:w-1/4"
          direction="flex-col"
        />
      </Field>
      <Field :rules="validEmail" name="email" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('email')"
          :is-read-only="isReadOnly"
          class="px-1.5 w-full md:w-1/2"
          direction="flex-col"
        />
      </Field>
    </div>
    <div class="flex flex-col md:flex-row">
      <Field
        :rules="requiredRule"
        name="mobileNumber"
        v-slot="{ field, errors: errs, handleChange }"
      >
        <ComboText
          :field="field"
          :errors="errs"
          :label="$t('indivUaeMobileNumber')"
          :options="addressMgmtStore.countries"
          :is-prefix-disabled="true"
          :is-read-only="isReadOnly"
          input-class="px-3 py-1.5"
          class="px-1.5 w-full md:w-1/2"
          direction="flex-col"
          @on-selection="(phone) => handleChange(phone)"
        ></ComboText>
      </Field>
      <Field
        :rules="requiredRule"
        name="phoneNumber"
        v-slot="{ field, errors: errs, handleChange }"
      >
        <ComboText
          :field="field"
          :errors="errs"
          :label="$t('fixedAbroadMobile')"
          :options="addressMgmtStore.countries"
          :is-prefix-disabled="true"
          :is-read-only="isReadOnly"
          input-class="px-3 py-1.5"
          class="px-1.5 w-full md:w-1/2"
          direction="flex-col"
          @on-selection="(phone) => handleChange(phone)"
        ></ComboText>
      </Field>
    </div>
    <div class="flex flex-col md:flex-row">
      <Field :rules="requiredRule" name="enAddressDetails" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('addressEnglishDescription')"
          :is-read-only="isReadOnly"
          class="px-1.5 w-full md:w-1/2"
          direction="flex-col"
          @on-blur="translateField(field.value, setFieldValue, 'arAddressDetails')"
        />
      </Field>
      <Field :rules="requiredRule" name="arAddressDetails" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('addressArabicDescription')"
          :is-read-only="isReadOnly"
          class="px-1.5 w-full md:w-1/2"
          direction="flex-col"
        />
      </Field>
    </div>
    <div v-if="!isReadOnly" class="w-full flex justify-between">
      <button
        class="text-center rounded-md bg-brand-gray-600/80 hover:bg-brand-gray-600 flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
        @click="$emit('onCancel')"
      >
        <div class="p-2 bg-brand-gray-600 rounded-s-md">
          <faTimes class="w-4 fill-white"></faTimes>
        </div>
        <span class="w-full text-center">
          {{ $t('Cancel') }}
        </span>
        <div class="p-0.5"></div>
      </button>
      <button
        class="min-w-28 text-center rounded-md bg-primary/80 pe-2 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
      >
        <div class="p-2 bg-primary rounded-s-md">
          <faDiskette class="w-4 fill-white"></faDiskette>
        </div>
        <span class="w-full text-center">
          {{ $t('Save') }}
        </span>
      </button>
    </div>
  </Form>
</template>

<script setup>
import faTimes from '@/assets/icons/faTimes.svg?component'
import faDiskette from '@/assets/icons/faDiskette.svg?component'
import ComboText from '@/components/SimpleForm/ComboText.vue'
import Dropdown from '@/components/SimpleForm/Dropdown.vue'
import TextInput from '@/components/SimpleForm/TextInput.vue'
import { isValidEmail } from '@/composables/useRuleHandler.js'
import axiosInstance from '@/services'
import { useIndAddressMgmtStore } from '@/stores/ind_address_mgmt.js'
import { Field, Form } from 'vee-validate'
import { useI18n } from 'vue-i18n'

const i18n = useI18n()
const addressMgmtStore = useIndAddressMgmtStore()

defineProps({
  isReadOnly: Boolean,
  initialValues: {
    type: Object,
    default: () => {
      return {
        mobileNumber: '00971',
        phoneNumber: '00971'
      }
    }
  }
})

function requiredRule(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return true
}

function validEmail(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return isValidEmail(value) || 'INVALID_EMAIL'
}

async function translateField(source, setFieldValue, destination) {
  const response = await axiosInstance.get(`/api/translation/${source}`)
  if (!response) return
  setFieldValue(destination, JSON.parse(response)[source.toLowerCase()])
}
</script>

<style lang="scss" scoped></style>
